/**
 * FAQ shown only on the Info page — organizational/donation questions,
 * deliberately separate from content/{lang}/larp-erleben/faq.md (which
 * covers event-participation questions). Real content provided directly
 * by the association. The "Wer steht hinter Tales&Legends?" answer is
 * consistent with the real Gesellschaftsvertrag (§ 3 names
 * BrainEntertainments Holding UG as the sole Gesellschafter; § 9 bars any
 * profit distribution to it) — no fabrication, matches facts already
 * published elsewhere on this site (Impressum, Gesellschaftsvertrag page).
 *
 * Grouped into categories (per user request) — grouping itself, not just
 * the underlying Q&A content.
 */
export const INFO_FAQ = {
  de: [
    {
      category: "Spenden",
      items: [
        {
          question: "Bekomme ich für meine Spende eine Bescheinigung?",
          answer:
            "Ja. Da Tales&Legends als gemeinnützig anerkannt ist, können wir Zuwendungsbescheinigungen ausstellen. Für Spenden bis 300 € reicht auch ein vereinfachter Nachweis (z. B. dein Kontoauszug). Trotzdem stellen wir automatisch für jede Spende eine Bescheinigung aus. Diese erhältst du dann per E-Mail.",
        },
        {
          question: "Wie wird meine Spende konkret verwendet?",
          answer:
            "Spenden fließen direkt in den Betrieb von Tales&Legends: in den Freiplatz-Fonds, in Veranstaltungskosten und in das, was nötig ist, damit alles läuft. Wir halten den Verwaltungsaufwand so gering wie möglich, damit möglichst viel dort ankommt, wo es wirkt. Du kannst über unsere Spendenseite selbst mitentscheiden, wofür wir deine Spende verwenden sollen.",
        },
        {
          question: "Gibt es einen Jahresbericht oder eine Übersicht über Einnahmen und Ausgaben?",
          answer:
            "Ja — wir legen offen, was reinkommt und was rausgeht. Die aktuelle Übersicht findest du weiter oben auf dieser Seite im Bereich Transparenz.",
        },
        {
          question: "Unser Unternehmen möchte sponsern oder Material spenden — wie geht das?",
          answer:
            "Sehr gerne! Schreib uns einfach eine Nachricht über das Kontaktformular oder per E-Mail, und wir melden uns zeitnah bei euch. Wir freuen uns über beides — finanzielle Unterstützung genauso wie Sachspenden.",
        },
      ],
    },
    {
      category: "Freiplätze",
      items: [
        {
          question: "Was ist der Freiplatz-Fonds?",
          answer:
            "Ist ein Spendenprojekt von Tales&Legends, alle Spenden, die über dieses Projekt reinkommen, gehen zu 100 % in freie oder ermäßigte Tickets für Menschen, die sich Events bei uns sonst nicht leisten könnten.",
        },
        {
          question: "Wie kann ich einen Freiplatz beantragen?",
          answer:
            "Schreib uns einfach eine Nachricht über das Kontaktformular oder direkt per E-Mail. Wir vergeben Freiplätze in der Reihenfolge der Anfragen, solange der Fonds es hergibt — also: lieber früh melden.",
        },
        {
          question: "Wer entscheidet, wer einen Freiplatz bekommt?",
          answer:
            "Ganz einfach: wer zuerst fragt. Wir prüfen keine Bedürftigkeit und stellen keine Bedingungen — du brauchst dir auch nichts erklären. Der Fonds ist für alle da, die sonst nicht dabei sein könnten.",
        },
      ],
    },
    {
      category: "Über Tales&Legends",
      items: [
        {
          question: "Ist Tales&Legends als gemeinnützig anerkannt?",
          answer:
            "Ja. Tales&Legends ist eine eingetragene gemeinnützige Unternehmergesellschaft (gUG) und vom Finanzamt als gemeinnützig anerkannt. Spenden sind entsprechend steuerlich absetzbar.",
        },
        {
          question: "Wer steht hinter Tales&Legends?",
          answer:
            "Tales&Legends ist eine gemeinnützige Unternehmergesellschaft (gUG) mit Sitz in Deutschland. Hinter dem Projekt steht ein kleines, ehrenamtlich geprägtes Team, das Geschichten und Gemeinschaft zusammenbringen möchte.\n\nDie gUG gehört zur BrainEntertainments Holding — doch das ändert nichts daran, wohin das Geld fließt: Aufgrund des Gemeinnützigkeitsstatus und § 9 Abs. 2 des Gesellschaftsvertrags dürfen keinerlei Mittel an die Holding abgeführt werden. Alles, was reinkommt, bleibt im Projekt und kommt der Gemeinschaft zugute.",
        },
      ],
    },
    {
      category: "Mitmachen & Kontakt",
      items: [
        {
          question: "Kann ich ehrenamtlich mithelfen?",
          answer:
            "Sehr gerne! Meld dich einfach bei uns — per Kontaktformular oder E-Mail — und erzähl uns kurz, womit du helfen möchtest oder kannst. Wir freuen uns über jede Hand.",
        },
        {
          question: "Wie erfahre ich von kommenden Veranstaltungen?",
          answer:
            "Am besten über unseren Newsletter und unsere Social-Media-Kanäle. Dort kündigen wir neue Termine, Tickets und alles Wichtige zuerst an.",
        },
        {
          question: "Ich habe eine andere Frage.",
          answer: "Kein Problem — schreib uns einfach über das Kontaktformular und wir antworten so schnell wie möglich.",
        },
      ],
    },
  ],
  en: [
    {
      category: "Donations",
      items: [
        {
          question: "Will I get a receipt for my donation?",
          answer:
            "Yes. Since Tales&Legends is recognized as non-profit, we can issue donation receipts. For donations up to €300, a simplified proof (e.g. your bank statement) is enough under German tax rules. Even so, we automatically issue a receipt for every donation. You'll receive it by email.",
        },
        {
          question: "How exactly is my donation used?",
          answer:
            "Donations go directly into running Tales&Legends: the Freiplatz-Fonds, event costs, and whatever's needed to keep things running. We keep administrative overhead as low as possible so as much as possible reaches where it makes a difference. On our donations page, you can help decide yourself what your donation goes toward.",
        },
        {
          question: "Is there an annual report or an overview of income and expenses?",
          answer: "Yes — we publish what comes in and what goes out. You'll find the current overview further up this page, under Transparency.",
        },
        {
          question: "Our business wants to sponsor or donate materials — how does that work?",
          answer:
            "We'd love that! Just send us a message via the contact form or by email, and we'll get back to you promptly. We welcome both — financial support as well as in-kind donations.",
        },
      ],
    },
    {
      category: "Free Tickets",
      items: [
        {
          question: "What is the Freiplatz-Fonds (free-ticket fund)?",
          answer:
            "It's one of Tales&Legends' donation projects — every donation that comes in through it goes 100% toward free or discounted tickets for people who couldn't otherwise afford to join our events.",
        },
        {
          question: "How can I apply for a free ticket?",
          answer:
            "Just send us a message via the contact form or directly by email. We allocate free tickets in the order requests come in, as long as the fund allows — so it's better to reach out early.",
        },
        {
          question: "Who decides who gets a free ticket?",
          answer:
            "Simple: whoever asks first. We don't check need and don't set conditions — you don't have to explain yourself either. The fund is there for anyone who couldn't otherwise take part.",
        },
      ],
    },
    {
      category: "About Tales&Legends",
      items: [
        {
          question: "Is Tales&Legends recognized as non-profit?",
          answer:
            "Yes. Tales&Legends is a registered non-profit UG (gUG) and is recognized as non-profit by the tax office. Donations are tax-deductible accordingly.",
        },
        {
          question: "Who's behind Tales&Legends?",
          answer:
            "Tales&Legends is a non-profit UG (gUG) based in Germany. Behind the project is a small, largely volunteer-run team that wants to bring stories and community together.\n\nThe gUG belongs to BrainEntertainments Holding — but that doesn't change where the money goes: because of our non-profit status and § 9(2) of the Gesellschaftsvertrag (articles of association), no funds may be passed on to the holding company. Everything that comes in stays in the project and benefits the community.",
        },
      ],
    },
    {
      category: "Get Involved & Contact",
      items: [
        {
          question: "Can I help out as a volunteer?",
          answer:
            "We'd love that! Just reach out — via the contact form or email — and tell us briefly what you'd like to or can help with. We're happy about every extra pair of hands.",
        },
        {
          question: "How do I find out about upcoming events?",
          answer: "Best via our newsletter and our social media channels. That's where we announce new dates, tickets, and anything important first.",
        },
        {
          question: "I have a different question.",
          answer: "No problem — just write to us via the contact form and we'll get back to you as soon as possible.",
        },
      ],
    },
  ],
};
